import type { ReactNode } from 'react'
import { Fragment, useMemo } from 'react'

import { Tooltip } from '@/components/ui/tooltip.tsx'
import { detectVariant } from '@/modules/message/components/placeholder/variants'
import { classNames } from '@/utils/helpers.ts'

interface PlaceholderTextPreviewProps {
  text: string
  className?: string
  showTooltip?: boolean
}

const MAX_ITERATIONS = 1000

export const PlaceholderTextPreview = ({
  text,
  className,
  showTooltip = true,
}: PlaceholderTextPreviewProps) => {
  const components = useMemo(
    () => {
      const pattern = /(%([a-zA-Z0-9 _-]+)%)/gm
      const parts: ReactNode[] = []
      let matches: RegExpExecArray | null = null
      let start = 0

      for (let count = 0; count < MAX_ITERATIONS; count++) {
        matches = pattern.exec(text)

        if (matches === null) {
          break
        }

        const full = matches[0] ?? ''
        const value = matches[2] ?? ''
        const before = text.substring(start, matches.index)

        if (before.length !== 0) {
          parts.push(<Fragment key={count}>{before}</Fragment>)
        }

        if (value.length !== 0) {
          parts.push(
            <MessageTextPreviewPlaceholder
              key={`${count}-var`}
              name={value}
              showTooltip={showTooltip}
            />,
          )
        }

        start = matches.index + full.length
      }

      const tail = text.substring(start)

      if (tail.length !== 0) {
        parts.push(<Fragment key="tail">{tail}</Fragment>)
      }

      return parts
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [text],
  )

  return <span className={classNames('truncate', className)}>{components}</span>
}

const MessageTextPreviewPlaceholder = ({
  name,
  showTooltip,
}: {
  name: string
  showTooltip?: boolean
}) => {
  const variant = useMemo(() => detectVariant(name), [name])

  if (showTooltip) {
    return (
      <Tooltip content={variant?.tooltip ?? 'Unknown variable'}>
        <span className="px-1">
          <span className="text-sm py-1 px-2 bg-secondary-50 dark:bg-secondary-500 border border-border-secondary rounded-md dark:text-secondary-100 hover:opacity-80 cursor-pointer">
            {variant?.name ?? name}
          </span>
        </span>
      </Tooltip>
    )
  }

  return (
    <span className="px-1">
      <span className="text-sm py-1 px-2 bg-secondary-50 dark:bg-secondary-500 border border-border-secondary rounded-md dark:text-secondary-100 hover:opacity-80 cursor-pointer">
        {variant?.name ?? name}
      </span>
    </span>
  )
}
